<script setup>
import { computed } from 'vue'

const props = defineProps({
    item: {
        type: Object,
        required: true
    },
    isChild: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['edit', 'remove', 'addChild'])

const kinds = {
    dropdown: {
        label: _mft('setting:navigation.type.dropdown'),
        icon: 'fa-regular fa-folder-open',
        badge: 'bg-gray-100 text-gray-700',
    },
    page: {
        label: _mft('setting:navigation.type.page'),
        icon: 'fa-regular fa-file-lines',
        badge: 'bg-sky-50 text-sky-700',
    },
    course: {
        label: _mft('setting:navigation.type.course'),
        icon: 'fa-regular fa-graduation-cap',
        badge: 'bg-violet-50 text-violet-700',
    },
    coaching: {
        label: _mft('setting:navigation.type.coaching'),
        icon: 'fa-regular fa-calendar-check',
        badge: 'bg-amber-50 text-amber-700',
    },
    subscription: {
        label: _mft('setting:navigation.type.subscription'),
        icon: 'fa-regular fa-repeat',
        badge: 'bg-emerald-50 text-emerald-700',
    },
    link: {
        label: _mft('setting:navigation.type.link'),
        icon: 'fa-regular fa-link',
        badge: 'bg-gray-100 text-gray-700',
    },
}

const childCount = computed(() => props.item.children?.length ?? 0)

const kind = computed(() => {
    if (childCount.value > 0) {
        return kinds.dropdown
    }
    if (props.item.item_page_id) {
        return kinds.page
    }
    if (props.item.item_course_id) {
        return kinds.course
    }
    if (props.item.item_coaching_product_id) {
        return kinds.coaching
    }
    if (props.item.item_subscribable_id) {
        return kinds.subscription
    }
    return kinds.link
})

const displayPath = computed(() => {
    if (!props.item.href) {
        return ''
    }
    try {
        const url = new URL(props.item.href, window.location.origin)
        return url.origin === window.location.origin ? url.pathname : url.href
    } catch (e) {
        return props.item.href
    }
})
</script>

<template>
    <div
        class="group flex items-center gap-3 py-2.5 pr-2 transition-colors hover:bg-gray-50"
        :class="isChild ? 'pl-2' : 'pl-3'"
    >
        <button
            type="button"
            class="nav-drag-handle -ml-1 inline-flex h-7 w-5 shrink-0 cursor-grab items-center justify-center text-gray-300 hover:text-gray-500 active:cursor-grabbing"
            :title="_mft('setting:navigation.item.dragToReorder')"
            :aria-label="_mft('setting:navigation.item.dragToReorder')"
        >
            <i class="fa-solid fa-grip-vertical text-sm"></i>
        </button>

        <i
            class="w-4 shrink-0 text-center text-sm text-gray-400"
            :class="kind.icon"
            aria-hidden="true"
        ></i>

        <div class="min-w-0 flex-1">
            <div
                class="truncate text-sm text-gray-900"
                :class="isChild ? '' : 'font-medium'"
            >{{ item.label }}</div>
            <div
                v-if="displayPath"
                class="truncate font-mono text-xs text-gray-400"
            >{{ displayPath }}</div>
        </div>

        <span
            class="hidden shrink-0 rounded-full px-2 py-0.5 text-xs sm:inline-block"
            :class="kind.badge"
        >
            {{ kind.label }}<template v-if="childCount > 0"> · {{ childCount }}</template>
        </span>

        <div class="flex shrink-0 items-center gap-0.5 transition-opacity md:opacity-40 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
            <button
                v-if="!isChild"
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                :title="_mft('setting:navigation.item.addChildItem')"
                :aria-label="_mft('setting:navigation.item.addChildItem')"
                @click="emit('addChild')"
            >
                <i class="fa-regular fa-plus"></i>
            </button>
            <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                :title="_mft('shared:action.edit')"
                :aria-label="_mft('shared:action.edit')"
                @click="emit('edit')"
            >
                <i class="fa-regular fa-pen"></i>
            </button>
            <button-with-confirmation
                v-if="childCount > 0"
                size="sm"
                :confirm-text="_mft('setting:navigation.item.remove.confirm')"
                :confirm-button-label="_mft('setting:navigation.item.remove')"
                @confirmed="emit('remove')"
                :title="_mft('setting:navigation.item.remove')"
            >
                <i class="fa-regular fa-trash text-red-500"></i>
            </button-with-confirmation>
            <button-with-confirmation
                v-else
                size="sm"
                class="whitespace-nowrap"
                :confirm-text="_mft('shared:confirm.areYouSure')"
                :confirm-button-label="_mft('setting:navigation.item.remove')"
                @confirmed="emit('remove')"
                :title="_mft('setting:navigation.item.remove')"
            >
                <i class="fa-regular fa-trash text-red-500"></i>
            </button-with-confirmation>
        </div>
    </div>
</template>
